import { BreadcrumbItem } from '@/types/services';

export const CONTENTSET_TITLE = 'コネクタ活用編';
export const BASE_URL = '/spot-live/connector-guide';

export type connectorOverViewItem = {
    connector_name: string;
    description: string;
    connector_detail_1: string;
    connector_detail_2: string;
};

export type usageTipsItem = {
    title: string;
    tips: string[];
};
export type useCaseItem = {
    title: string;
    description: string;
    use_case_detail: string;
    imageWidth: number;
};
export type codeExampleItem = {
    images: imageItem[];
};

export type imageItem = {
    title: string;
    codeBlockFileName?: string;
    connectionFileName?: string;
    baseWidth: number;
    lgWidth: number;
};

export type connectorGuideDataItem = {
    contentsCd: string;
    connectorType?: ConnectorType;
    connectorOverView: connectorOverViewItem;
    usageTips?: usageTipsItem;
    useCase?: useCaseItem;
    codeExample?: codeExampleItem;
    showTutorialVideo: boolean;
};

export type ConnectorType =
    | 'touch-sensor'
    | 'switch'
    | 'light-sensor'
    | 'magnet-sensor'
    | 'pass-sensor'
    | 'sound-sensor'
    | 'ir-recv-sensor'
    | 'soil-sensor'
    | 'temp-sensor'
    | 'accel-sensor'
    | 'distance-sensor'
    | 'led'
    | '7seg'
    | 'rgb-led'
    | 'ir-send'
    | 'speaker'
    | 'vibration-switch'
    | 'usb'
    | 'rain-sensor'
    | 'moisture-sensor'
    | 'barometric-pressure-sensor'
    | 'water-temperature-sensor'
    | 'presence-sensor'
    | 'pressure-sensor'
    | 'alcohol-sensor'
    | 'flex-sensor'
    | 'gps-sensor'
    | 'water-flow-sensor'
    | 'ammonia-sensor'
    | 'toggle-switch'
    | 'slide-switch'
    | 'fader'
    | 'vibration-motor'
    | 'propeller-motor'
    | 'oled'
    | 'servo-motor-180'
    | 'pump'
    | 'led-bar'
    | 'tt-motor'
    | 'sd-card';

export const contentCdMap: Record<ConnectorType, string> = {
    'touch-sensor': '01',
    switch: '02',
    'light-sensor': '03',
    'magnet-sensor': '04',
    'pass-sensor': '05',
    'sound-sensor': '06',
    'ir-recv-sensor': '07',
    'soil-sensor': '08',
    'temp-sensor': '09',
    'accel-sensor': '10',
    'distance-sensor': '11',
    led: '12',
    '7seg': '13',
    'rgb-led': '14',
    'ir-send': '15',
    speaker: '16',
    'vibration-switch': '17',
    usb: '18',
    'rain-sensor': '19',
    'moisture-sensor': '20',
    'barometric-pressure-sensor': '21',
    'water-temperature-sensor': '22',
    'presence-sensor': '23',
    'pressure-sensor': '24',
    'alcohol-sensor': '25',
    'flex-sensor': '26',
    'gps-sensor': '27',
    'water-flow-sensor': '28',
    'ammonia-sensor': '29',
    'toggle-switch': '30',
    'slide-switch': '31',
    fader: '32',
    'vibration-motor': '33',
    'propeller-motor': '34',
    oled: '35',
    'servo-motor-180': '36',
    pump: '37',
    'led-bar': '38',
    'tt-motor': '39',
    'sd-card': '40',
};

export const CONNECTOR_GUIDE_DATA: readonly connectorGuideDataItem[] = [
    {
        contentsCd: '01',
        connectorType: 'touch-sensor',
        showTutorialVideo: true,
        connectorOverView: {
            connector_name: 'タッチセンサー',
            description: 'スマートフォンなどのタッチ画面の仕組み',
            connector_detail_1: 'センサーの銀の部分に指がふれることで0から1に数値が変化します',
            connector_detail_2: '静電容量（せいでんようりょう）の変化を読み取っています',
        },
        usageTips: {
            title: 'アルミホイル以外にも反応します',
            tips: ['葉のように、水分をふくむもの', 'はさみのように、金属部分があるもの'],
        },
        useCase: {
            title: '楽しんでもらうためのモノづくり',
            use_case_detail: '触れたらアウトなゲーム',
            description: `タッチセンサーに触れると反応する仕組みを利用し、アルミホイルを活用したゲームが作れます。
            触れると音がなる・光る・開くなど、触れた時のごほうびをどんなふうにするかなどのパワーアップ`,
            imageWidth: 280,
        },
        codeExample: {
            images: [
                {
                    title: 'タッチしたらランダムに1から5の数字が表示される',
                    codeBlockFileName: 'code-1-l1.png',
                    connectionFileName: 'code-1-r1.png',
                    baseWidth: 260,
                    lgWidth: 520,
                },
                {
                    title: 'タッチしたらランダムに111から555の数字が表示される',
                    codeBlockFileName: 'code-2-l1.png',
                    baseWidth: 260,
                    lgWidth: 650,
                },
                {
                    title: `タッチしたらランダムに111から555の数字が表示されて、ゾロ目がそろったら赤色に光り、そろっていないときは青色に光る`,
                    codeBlockFileName: 'code-3-l1.png',
                    connectionFileName: 'code-3-r1.png',
                    baseWidth: 260,
                    lgWidth: 650,
                },
            ],
        },
    },
    {
        contentsCd: '02',
        connectorType: 'switch',
        showTutorialVideo: true,
        connectorOverView: {
            connector_name: 'スイッチ',
            description: '早押しボタンや横断歩道にあるボタンなどの「おしボタン」',
            connector_detail_1: 'おされると0から1に値が変化します',
            connector_detail_2: 'おされたことで、電気の通り道ができて、電気が流れたことを判定しています',
        },
        usageTips: {
            title: 'それぞれのおしかたで反応させることができます',
            tips: [
                '人がおすだけではなく、何かが起きたことで、ものが動きます。',
                'その動いたものがスイッチにあたるなどオリジナルスイッチをつくることができます',
            ],
        },
        useCase: {
            title: '運動不足を解消するためのモノづくり',
            use_case_detail: '起動システム',
            description: `システムの起動や終了をスイッチで行うことでシステムの開始をわかりやすくする。
スイッチを押してからシステムを起動させ、距離センサーに足を近づけることでスタートをする仕組み。`,
            imageWidth: 280,
        },
        codeExample: {
            images: [
                {
                    title: 'スイッチを押したときモニターに1が表示されることを確認する',
                    codeBlockFileName: 'code-1-l1.png',
                    connectionFileName: 'code-1-r1.png',
                    baseWidth: 260,
                    lgWidth: 520,
                },
                {
                    title: 'スイッチを押したらLEDが光るようにする',
                    codeBlockFileName: 'code-2-l1.png',
                    connectionFileName: 'code-2-r1.png',
                    baseWidth: 260,
                    lgWidth: 520,
                },
                {
                    title: 'スイッチを押したら光り、もう一度押したら消える',
                    codeBlockFileName: 'code-3-l1.png',
                    connectionFileName: 'code-3-r1.png',
                    baseWidth: 260,
                    lgWidth: 610,
                },
            ],
        },
    },
    {
        contentsCd: '03',
        connectorType: 'light-sensor',
        showTutorialVideo: true,
        connectorOverView: {
            connector_name: '明るさセンサー',
            description: 'パソコンなどで、周りが暗いと画面が明るくなる仕組み',
            connector_detail_1: 'どのくらいセンサーに光が当たっているかを見ることができます',
            connector_detail_2: 'Cdsセルとよばれることもあります',
        },
        usageTips: {
            title: '明るさの変化がわかると、こんなこともわかります',
            tips: ['明暗', '透明度', '回転', '開閉', '影', '色'],
        },
        useCase: {
            title: '暮らしやすい便利をつくるためのモノづくり',
            description:
                '明るさセンサーと距離センサーの２つを活用し、部屋が暗い時に段差に近づいたら、足元が光注意を促す仕組み',
            use_case_detail: '暗い部屋で段差に近づいたら、光って教えてくれる',
            imageWidth: 360,
        },
        codeExample: {
            images: [
                {
                    title: 'センサーの周りを10Luxになるように暗くしよう',
                    codeBlockFileName: 'code-1-l1.png',
                    connectionFileName: 'code-1-r1.png',
                    baseWidth: 260,
                    lgWidth: 500,
                },
                {
                    title: '10Luxより小さくなったときに、LEDが光るようにしよう',
                    codeBlockFileName: 'code-2-l1.png',
                    connectionFileName: 'code-2-r1.png',
                    baseWidth: 260,
                    lgWidth: 500,
                },
                {
                    title: '5秒間暗くなったらスピーカーから音を鳴らそう',
                    codeBlockFileName: 'code-3-l1.png',
                    connectionFileName: 'code-3-r1.png',
                    baseWidth: 260,
                    lgWidth: 650,
                },
            ],
        },
    },
    {
        contentsCd: '04',
        connectorType: 'magnet-sensor',
        showTutorialVideo: true,
        connectorOverView: {
            connector_name: '磁気センサー',
            description: 'エアコンや冷蔵庫などの家電製品で利用される仕組み',
            connector_detail_1: '磁力に反応します。N極、S極で反応が変わります',
            connector_detail_2: 'ホール効果を利用しているので、ホールセンサーとも呼ばれます',
        },
        usageTips: {
            title: '磁力の変化がわかると、こんなこともわかります',
            tips: ['回転', '高さ', '開閉'],
        },
        useCase: {
            title: '暮らしやすい便利をつくるためのモノづくり',
            use_case_detail: '自宅の鍵を閉めたか、スマホから確認できる',
            description: `あれ？鍵かけたっけ？という外出してからの不安を、家の外にいても鍵がかかったかどうかをスマートフォンから確認できる仕組み
                        線の回る部分に磁石をつけて、閉まっているか開いているかをセンサーとの距離で調べる。`,
            imageWidth: 360,
        },
        codeExample: {
            images: [
                {
                    title: '磁石を近づけたときの数値の変化を確認する',
                    codeBlockFileName: 'code-1-l1.png',
                    connectionFileName: 'code-1-r1.png',
                    baseWidth: 260,
                    lgWidth: 520,
                },
                {
                    title: '磁石を近づけたらLEDが光るようにする',
                    codeBlockFileName: 'code-2-l1.png',
                    connectionFileName: 'code-2-r1.png',
                    baseWidth: 260,
                    lgWidth: 520,
                },
                {
                    title: '磁石が5秒間離れたらLEDが光る',
                    codeBlockFileName: 'code-3-l1.png',
                    connectionFileName: 'code-3-r1.png',
                    baseWidth: 260,
                    lgWidth: 520,
                },
            ],
        },
    },
    {
        contentsCd: '05',
        connectorType: 'pass-sensor',
        showTutorialVideo: true,
        connectorOverView: {
            connector_name: '通過センサー',
            description: '紙やコインなどのうすいものを通過して反応させる',
            connector_detail_1: 'うすいモノの通過を調べることができます',
            connector_detail_2: '回転や開閉を検知することができます',
        },
        useCase: {
            title: '貯金をするためのモノづくり',
            use_case_detail: '貯金箱をデジタル化',
            description: `貯金箱のお金を入れる口の部分につけることによって、お金が入った回数やその回数によってアクションを起こしたり、一定時間お金が入っていないことを伝えることができる仕組み`,
            imageWidth: 280,
        },
        codeExample: {
            images: [
                {
                    title: 'センサーにカードを通過させたときモニターに1が表示されることを確認する',
                    codeBlockFileName: 'code-1-l1.png',
                    connectionFileName: 'code-1-r1.png',
                    baseWidth: 260,
                    lgWidth: 540,
                },
                {
                    title: '通過した回数をカウントする',
                    codeBlockFileName: 'code-2-l1.png',
                    connectionFileName: 'code-2-r1.png',
                    baseWidth: 260,
                    lgWidth: 610,
                },
                {
                    title: '通過した回数が5回以上ならLEDが光る',
                    codeBlockFileName: 'code-3-l1.png',
                    connectionFileName: 'code-3-r1.png',
                    baseWidth: 260,
                    lgWidth: 610,
                },
            ],
        },
    },
    {
        contentsCd: '06',
        connectorType: 'sound-sensor',
        showTutorialVideo: true,
        connectorOverView: {
            connector_name: '音センサー',
            description: 'スピーカーや音声入力できる仕組み',
            connector_detail_1: '音の大きさ・小ささを読み取ることができます',
            connector_detail_2: '音によって生成された音波がセンサーの中の部品にあたる必要があります',
        },
        usageTips: {
            title: '音の大きさ・小ささをグラフで見ることができます',
            tips: [
                '音の高さ・低さは読み取りにくいです',
                'スクーミーボードをパソコンにつなげてグラフをパソコンに表示させます',
            ],
        },
        useCase: {
            title: '聞き取りにくいを解決するためのモノづくり',
            use_case_detail: '体温計の音を感知するシステム',
            description: `体温計の音は小さく高齢者には聞き取りづらいという課題を、音センサーを活用して、音が鳴ったらLEDが光る仕組みをつくり、体温を測り終えたら光で合図をしてくれる仕組み`,
            imageWidth: 280,
        },
        codeExample: {
            images: [
                {
                    title: 'センサーの前で話したときのグラフの波形を確認する',
                    codeBlockFileName: 'code-1-l1.png',
                    connectionFileName: 'code-1-r1.png',
                    baseWidth: 260,
                    lgWidth: 520,
                },
                {
                    title: '音が大きかったらLEDが光るようにする',
                    codeBlockFileName: 'code-2-l1.png',
                    connectionFileName: 'code-2-r1.png',
                    baseWidth: 260,
                    lgWidth: 520,
                },
                {
                    title: '大きい音が3秒続いたら、LEDが光るようにする',
                    codeBlockFileName: 'code-3-l1.png',
                    connectionFileName: 'code-3-r1.png',
                    baseWidth: 260,
                    lgWidth: 610,
                },
            ],
        },
    },
    {
        contentsCd: '07',
        connectorType: 'ir-recv-sensor',
        showTutorialVideo: true,
        connectorOverView: {
            connector_name: '赤外線受信',
            description: 'リモコンなどから発せられる赤外線を読み取る',
            connector_detail_1: '赤外線通信の受信を行います',
            connector_detail_2: 'テレビやエアコンのリモコンの赤外線の情報を読み取ることができます',
        },
        useCase: {
            title: '学校の感染症対策のためのモノづくり',
            use_case_detail: '消毒管理システム',
            description: `消毒したことを圧力センサーで感知し、送信する。
            入り口などに設置し、圧力センサーにふれていないときは、消毒したことが確認できていないために、音がなる、注意をする。`,
            imageWidth: 280,
        },
        codeExample: {
            images: [
                {
                    title: '赤外線番号１を受信したらLEDが1秒光る。その後、消える',
                    codeBlockFileName: 'code-1-l1.png',
                    connectionFileName: 'code-1-r1.png',
                    baseWidth: 260,
                    lgWidth: 520,
                },
                {
                    title: '赤外線番号１を受信したら変数iが1増えて、それが7SEGに表示される',
                    codeBlockFileName: 'code-2-l1.png',
                    connectionFileName: 'code-2-r1.png',
                    baseWidth: 260,
                    lgWidth: 520,
                },
                {
                    title: 'スイッチを押したら、ランダムで赤外線番号１から５が送信される',
                    codeBlockFileName: 'code-3-l1.png',
                    connectionFileName: 'code-3-r1.png',
                    baseWidth: 260,
                    lgWidth: 520,
                },
            ],
        },
    },
    {
        contentsCd: '08',
        connectorType: 'soil-sensor',
        showTutorialVideo: true,
        connectorOverView: {
            connector_name: '土壌水分センサー',
            description: '土の水分量を調べて、その土壌に適した作物を育てる仕組み',
            connector_detail_1: '土の中の水分量を測ることができます',
            connector_detail_2: '水がない状態を0%、水しかない状態を100%としています',
        },
        usageTips: {
            title: '水分量の変化をグラフで見ることができます',
            tips: ['スクーミーボードをパソコンにつなげておくことでグラフをパソコンに表示させます'],
        },
        useCase: {
            title: '実験とモノづくり',
            use_case_detail: '毛細管現象',
            description: `水が上がってくる仕組みを視覚的にわかりやすくするために、
土壌水分センサーとティッシュペーパーを使って実験のサポートとしてセンサーを活用`,
            imageWidth: 360,
        },
        codeExample: {
            images: [
                {
                    title: 'センサーを土にさし、水をかけたときの数値の変化を確認する',
                    codeBlockFileName: 'code-1-l1.png',
                    connectionFileName: 'code-1-r1.png',
                    baseWidth: 260,
                    lgWidth: 610,
                },
                {
                    title: '水分量の割合が20%以下だったときにLEDが光るようにする',
                    codeBlockFileName: 'code-2-l1.png',
                    connectionFileName: 'code-2-r1.png',
                    baseWidth: 260,
                    lgWidth: 610,
                },
                {
                    title: '水分量の割合が30%より大きい場合は赤色、10%から30%のときは緑色、それ以下の時は青色に光る',
                    codeBlockFileName: 'code-3-l1.png',
                    connectionFileName: 'code-3-r1.png',
                    baseWidth: 260,
                    lgWidth: 610,
                },
            ],
        },
    },
    {
        contentsCd: '09',
        connectorType: 'temp-sensor',
        showTutorialVideo: true,
        connectorOverView: {
            connector_name: '温度センサー',
            description: '家電製品の温度が上がりすぎないようにするための仕組み',
            connector_detail_1: '黒い部分が温度の変化を読み取ることができます',
            connector_detail_2: '気温を測ったり、ふれたものの温度を測ることができます',
        },
        useCase: {
            title: '健康管理をするためのモノづくり',
            use_case_detail: '温度や不快指数、暑さ指数などを、ディスプレイ上で確認できる',
            description: `取得した温度をそのまま7SEGに表示することもできるが、
温度の値と湿度の値をセンサーで取得し、それらの値を計算することで、
不快指数や暑さ指数（熱中症指数・WBGT）などを出し、7SEGに表示することもできる`,
            imageWidth: 640,
        },
        codeExample: {
            images: [
                {
                    title: 'センサーを温めて30℃にしよう',
                    codeBlockFileName: 'code-1-l1.png',
                    connectionFileName: 'code-1-r1.png',
                    baseWidth: 260,
                    lgWidth: 520,
                },
                {
                    title: '30℃を超えたらLEDが光るようにする',
                    codeBlockFileName: 'code-2-l1.png',
                    connectionFileName: 'code-2-r1.png',
                    baseWidth: 260,
                    lgWidth: 520,
                },
                {
                    title: '30℃以上、もしくは、20℃以下のときに、赤色で点滅をする。それ以外のときは緑色に光る。',
                    codeBlockFileName: 'code-3-l1.png',
                    connectionFileName: 'code-3-r1.png',
                    baseWidth: 260,
                    lgWidth: 520,
                },
            ],
        },
    },
    {
        contentsCd: '10',
        connectorType: 'accel-sensor',
        showTutorialVideo: true,
        connectorOverView: {
            connector_name: '加速度・角度センサー',
            description: 'ボードがどれだけ動いたかを数値で示すことができる',
            connector_detail_1: 'ボードがどれだけ水平から傾いたかの角度がわかります',
            connector_detail_2: '止まっているときを1とした時どれだけ動いたかわかりますまた加速度の大きさを測定できます',
        },
        useCase: {
            title: '楽しんでもらうためのモノづくり',
            use_case_detail: 'テレビゲームを遊べる「手作りゲームコントローラー」',
            description: `加速度・角度センサーを動かすことで、テレビゲームのコントローラにすることができる。
            また自分でUnityなどでゲームをつくり、そのゲームのコントローラーにすることもできる。`,
            imageWidth: 280,
        },
        codeExample: {
            images: [
                {
                    title: '加速度の大きさが2を超えるようにセンサーを動かす',
                    codeBlockFileName: 'code-1-l1.png',
                    connectionFileName: 'code-1-r1.png',
                    baseWidth: 260,
                    lgWidth: 580,
                },
                {
                    title: '加速度の大きさが2を超えたらLEDが光るようにする',
                    codeBlockFileName: 'code-2-l1.png',
                    connectionFileName: 'code-2-r1.png',
                    baseWidth: 260,
                    lgWidth: 520,
                },
                {
                    title: '振った回数をカウントする',
                    codeBlockFileName: 'code-3-l1.png',
                    connectionFileName: 'code-3-r1.png',
                    baseWidth: 260,
                    lgWidth: 650,
                },
            ],
        },
    },
    {
        contentsCd: '11',
        connectorType: 'distance-sensor',
        showTutorialVideo: true,
        connectorOverView: {
            connector_name: '距離センサー',
            description: '自動運転の車の衝突（しょうとつ）を防ぐ仕組み',
            connector_detail_1: 'およそ2mくらいまでの距離を測ることができます',
            connector_detail_2: '超音波センサーとよばれることもあります',
        },
        usageTips: {
            title: '距離がわかると、こんなこともわかります',
            tips: ['量', '通過', '早さ', '高さ', '存在'],
        },
        useCase: {
            title: '運動不足を解消するためのモノづくり',
            use_case_detail: '腹筋の目標回数を達成すると音を鳴らせる',
            description: `腹筋ローラーと組み合わせて、音が鳴るまで近づくことで、回数を数えてくれ、目標数値まで達するとメロディが鳴る。サボりがちな筋トレを楽しくわかりやすくできるようにした仕組み`,
            imageWidth: 360,
        },
        codeExample: {
            images: [
                {
                    title: '距離が20センチになるようにセンサーの前にモノをおこう',
                    codeBlockFileName: 'code-1-l1.png',
                    connectionFileName: 'code-1-r1.png',
                    baseWidth: 260,
                    lgWidth: 610,
                },
                {
                    title: '10Luxより小さくなったときに、LEDが光るようにする',
                    codeBlockFileName: 'code-2-l1.png',
                    connectionFileName: 'code-2-r1.png',
                    baseWidth: 260,
                    lgWidth: 520,
                },
                {
                    title: 'センサーの前を通った人数をカウントする',
                    codeBlockFileName: 'code-3-l1.png',
                    connectionFileName: 'code-3-r1.png',
                    baseWidth: 260,
                    lgWidth: 610,
                },
            ],
        },
    },
    {
        contentsCd: '12',
        connectorType: 'led',
        showTutorialVideo: true,
        connectorOverView: {
            connector_name: 'LED',
            description: '光ることで知らせたり、楽しませたりする',
            connector_detail_1: '光ります',
            connector_detail_2: 'ずっと光ったり、ピカピカ光ったりします',
        },
        usageTips: {
            title: '光り方を工夫することで、声に出さなくてもメッセージを伝えることができます',
            tips: ['終わったかどうかを伝える', '使っているかどうかを伝える', '時間になりそうなことを伝える'],
        },
        useCase: {
            title: '視覚的にわかりやすくする仕組み',
            use_case_detail: 'コミュニケーションボード',
            description: `ボード上にLEDを複数個利用し、点滅するところを光らせて、文字で書くよりも視覚的にわかりやすく簡単に伝える仕組み。当番表や明日の連絡などを光で伝えることでコミュニケーション課題を解消する。`,
            imageWidth: 280,
        },
        codeExample: {
            images: [
                {
                    title: 'LEDを点滅させる',
                    codeBlockFileName: 'code-1-l1.png',
                    connectionFileName: 'code-1-r1.png',
                    baseWidth: 260,
                    lgWidth: 450,
                },
                {
                    title: 'LEDを交互に光らせる',
                    codeBlockFileName: 'code-2-l1.png',
                    connectionFileName: 'code-2-r1.png',
                    baseWidth: 260,
                    lgWidth: 450,
                },
                {
                    title: 'スイッチを押したらLEDが光り、もう一度スイッチを押すとLEDが消える',
                    codeBlockFileName: 'code-3-l1.png',
                    connectionFileName: 'code-3-r1.png',
                    baseWidth: 260,
                    lgWidth: 610,
                },
            ],
        },
    },
    {
        contentsCd: '13',
        connectorType: '7seg',
        showTutorialVideo: true,
        connectorOverView: {
            connector_name: '7SEG',
            description: '数字を表示する（9999まで）',
            connector_detail_1: 'センサーが取得する数字を表示することができます',
            connector_detail_2: '時間や個数など、カウントアップやダウンができます',
        },
        usageTips: {
            title: '個数や時間を見えるようにすることで、自分や他の人に、さまざまなことを伝えることができます',
            tips: [
                'そろそろ時間だ！という残り時間を伝える',
                '数が減ってきた!という残りの量を伝える',
                'これだけやった！という回数を伝える',
            ],
        },
        useCase: {
            title: '楽しませるためのモノづくり',
            use_case_detail: '4ケタの数字を揃える「うんだめしゲーム」',
            description: `温度や明るさを表示させるだけなく、人の通過の回数も表示させるときに利用する。
                        その中でもルーレットのように数字を３ケタで表示させ、ゾロ目を出したり、
                        決めておいた数字をランダムに出したりすることでゲーム性を持たせることができる。`,
            imageWidth: 280,
        },
        codeExample: {
            images: [
                {
                    title: '明るさを7SEGに表示する',
                    codeBlockFileName: 'code-1-l1.png',
                    connectionFileName: 'code-1-r1.png',
                    baseWidth: 260,
                    lgWidth: 520,
                },
                {
                    title: '変数を使って数字を表示する',
                    codeBlockFileName: 'code-2-l1.png',
                    connectionFileName: 'code-2-r1.png',
                    baseWidth: 260,
                    lgWidth: 520,
                },
                {
                    title: 'ストップウォッチをつくる',
                    codeBlockFileName: 'code-3-l1.png',
                    connectionFileName: 'code-3-r1.png',
                    baseWidth: 260,
                    lgWidth: 650,
                },
            ],
        },
    },
    {
        contentsCd: '14',
        connectorType: 'rgb-led',
        showTutorialVideo: true,
        connectorOverView: {
            connector_name: 'フルカラーLED',
            description: 'RGBの値を設定し、色を変化させて光らせることができる',
            connector_detail_1: 'RGB（Red, Green, Blue）の値を変更で色を自由につくることができます',
            connector_detail_2: '色が変化に変わる光らせ方ができ、その違いを使うことで工夫できます',
        },
        usageTips: {
            title: '光り方を工夫することで、声に出さなくてもメッセージを伝えることができます',
            tips: ['温度の変化', '水分量の変化', '正解・不正解', '危険度', '混雑状況', '音の大きさ・波形'],
        },
        useCase: {
            title: '感染症対策のためのモノづくり',
            use_case_detail: 'コロナ対策ボックス',
            description: `フルカラーLEDの色を変えることで、色の違いで「安全」・「危険」などを視覚的にわかりやすく伝える仕組み
            箱の中にはマスクやウェットティッシュを入れるスペースを用意`,
            imageWidth: 280,
        },
        codeExample: {
            images: [
                {
                    title: '赤、青、緑をグラデーションで光らせる',
                    codeBlockFileName: 'code-1-l1.png',
                    connectionFileName: 'code-1-r1.png',
                    baseWidth: 260,
                    lgWidth: 520,
                },
                {
                    title: '赤と青と緑を交互に光らせる',
                    codeBlockFileName: 'code-2-l1.png',
                    connectionFileName: 'code-2-r1.png',
                    baseWidth: 260,
                    lgWidth: 520,
                },
                {
                    title: 'タッチするたびに、赤、青、緑と光る',
                    codeBlockFileName: 'code-3-l1.png',
                    connectionFileName: 'code-3-r1.png',
                    baseWidth: 260,
                    lgWidth: 610,
                },
            ],
        },
    },
    {
        contentsCd: '15',
        connectorType: 'ir-send',
        showTutorialVideo: true,
        connectorOverView: {
            connector_name: '赤外線送信',
            description: '同じ赤外線を設定することでテレビなどを操作できる',
            connector_detail_1: '赤外線の送信を行います',
            connector_detail_2:
                'リモコンと同じ赤外線を発する設定をプログラムすることでテレビなどを操作することができます',
        },
        useCase: {
            title: '楽しんでもらうためのモノづくり',
            use_case_detail: '数値の変化で遊ぶ',
            description: `スイッチを押すことで、もう一方のスクーミーボードの7SEGの数値が変化するようにプログラムをし、シューティングゲームのように遊べる仕組み。数字に合わせてフルカラーLEDの色も変わる。`,
            imageWidth: 460,
        },
        codeExample: {
            images: [
                {
                    title: 'スイッチをおしたら、赤外線番号１が送信される',
                    codeBlockFileName: 'code-1-l1.png',
                    connectionFileName: 'code-1-r1.png',
                    baseWidth: 260,
                    lgWidth: 520,
                },
                {
                    title: '赤外線番号１から３までが順番に一定間隔で送信される',
                    codeBlockFileName: 'code-2-l1.png',
                    connectionFileName: 'code-2-r1.png',
                    baseWidth: 260,
                    lgWidth: 520,
                },
                {
                    title: 'スイッチを押したら、ランダムで赤外線番号１から５が送信される',
                    codeBlockFileName: 'code-3-l1.png',
                    connectionFileName: 'code-3-r1.png',
                    baseWidth: 260,
                    lgWidth: 520,
                },
            ],
        },
    },
    {
        contentsCd: '16',
        connectorType: 'speaker',
        showTutorialVideo: true,
        connectorOverView: {
            connector_name: 'スピーカー',
            description: '知らせる音・注意する音・楽しませる音などを出す',
            connector_detail_1: 'ドレミファ・・・でつくったメロディを出します',
            connector_detail_2: '＃やオクターブ、長さの設定をしたメロディを出します',
        },
        usageTips: {
            title: '音の長さやテンポを工夫することで、センサーでメッセージを伝えることができます',
            tips: ['あぶないことをアラートで注意する', '3分タイマーのアラームで伝える', 'サイン音で楽しくする'],
        },
        useCase: {
            title: '暮らしやすい便利をつくるためのモノづくり',
            use_case_detail: '水田の水が減ったら、音で知らせてくれる',
            description: `● 水面に磁石を浮かせておき、底の方に磁気センサーをおいておく状況をつくる。

                        ● 水が無くなってくる浮いている磁石が磁気センサーに近づき、一定の量水が減ったら音が鳴って知らせてくれる仕組み`,
            imageWidth: 280,
        },
        codeExample: {
            images: [
                {
                    title: 'ドの音を鳴らす',
                    codeBlockFileName: 'code-1-l1.png',
                    connectionFileName: 'code-1-r1.png',
                    baseWidth: 260,
                    lgWidth: 650,
                },
                {
                    title: '休符を使って、メロディをつくる',
                    codeBlockFileName: 'code-2-l1.png',
                    connectionFileName: 'code-2-r1.png',
                    baseWidth: 260,
                    lgWidth: 650,
                },
                {
                    title: '距離によって、音が変わるようにする',
                    codeBlockFileName: 'code-3-l1.png',
                    connectionFileName: 'code-3-r1.png',
                    baseWidth: 260,
                    lgWidth: 650,
                },
            ],
        },
    },

    {
        contentsCd: '17',
        showTutorialVideo: false,
        connectorOverView: {
            connector_name: '振動スイッチ',
            description: '振動しているかどうかを判断する',
            connector_detail_1: '振動スイッチでものが振動しているかしていないかを測ることができます',
            connector_detail_2: '振動センサーに揺れが伝わると値が1になります',
        },
        codeExample: {
            images: [
                {
                    title: 'スクーミーボードを振動させるとLEDが光るプログラムをつくる',
                    codeBlockFileName: 'code-1-l1.png',
                    connectionFileName: 'code-1-r1.png',
                    baseWidth: 260,
                    lgWidth: 650,
                },
                {
                    title: '揺れたことを7SEGに表示する',
                    codeBlockFileName: 'code-2-l1.png',
                    connectionFileName: 'code-2-r1.png',
                    baseWidth: 260,
                    lgWidth: 650,
                },
                {
                    title: '揺れが伝わったらLEDが光る',
                    codeBlockFileName: 'code-3-l1.png',
                    connectionFileName: 'code-3-r1.png',
                    baseWidth: 260,
                    lgWidth: 650,
                },
            ],
        },
    },
    {
        contentsCd: '18',

        showTutorialVideo: false,
        connectorOverView: {
            connector_name: 'USBコネクター',
            description: 'スクーミーボードにUSB-Aを接続して制御する',
            connector_detail_1: 'USB-Aを接続して動かすことができる機器を制御できます',
            connector_detail_2: 'LED照明や小型扇風機の電源をつけたり、消したりできます',
        },
        codeExample: {
            images: [
                {
                    title: '接続した USB-A を「ON」にする',
                    codeBlockFileName: 'code-1-l1.png',
                    connectionFileName: 'code-1-r1.png',
                    baseWidth: 260,
                    lgWidth: 650,
                },
                {
                    title: 'タッチセンサーに触れているときだけ USB-A を「ON」にする',
                    codeBlockFileName: 'code-2-l1.png',
                    connectionFileName: 'code-2-r1.png',
                    baseWidth: 260,
                    lgWidth: 650,
                },
            ],
        },
    },
    {
        contentsCd: '19',

        showTutorialVideo: false,
        connectorOverView: {
            connector_name: '雨センサー',
            description: '雨が降ったことを判断する',
            connector_detail_1: '電極間の導電性を利用して水滴を検知し、雨の降り具合を測ることができます',
            connector_detail_2: '水滴がセンサープレートに付着すると電気抵抗が低下し、数値が大きくなります',
        },
        codeExample: {
            images: [
                {
                    title: '雨が降ったかどうかをシリアルモニターで確認する',
                    codeBlockFileName: 'code-1-l1.png',
                    connectionFileName: 'code-1-r1.png',
                    baseWidth: 260,
                    lgWidth: 650,
                },
                {
                    title: '雨が降っていることを音でおしえるプログラムをつくる',
                    codeBlockFileName: 'code-2-l1.png',
                    connectionFileName: 'code-2-r1.png',
                    baseWidth: 260,
                    lgWidth: 650,
                },
            ],
        },
    },
    {
        contentsCd: '20',

        showTutorialVideo: false,
        connectorOverView: {
            connector_name: '湿度センサー',
            description: '空気中の湿度を測る',
            connector_detail_1: '湿度（空気中に含まれる水蒸気の割合）を測ることができます',
            connector_detail_2:
                '高湿度や低湿度のときにアラートを鳴らし、適切な湿度を保つセンサーをつくることができます',
        },
        codeExample: {
            images: [
                {
                    title: '湿度を測定する',
                    codeBlockFileName: 'code-1-l1.png',
                    connectionFileName: 'code-1-r1.png',
                    baseWidth: 260,
                    lgWidth: 650,
                },
                {
                    title: '湿度が上がったらアラートを鳴らす',
                    codeBlockFileName: 'code-2-l1.png',
                    connectionFileName: 'code-2-r1.png',
                    baseWidth: 260,
                    lgWidth: 650,
                },
                {
                    title: '湿度によってLEDの光り方を変える',
                    codeBlockFileName: 'code-3-l1.png',
                    connectionFileName: 'code-3-r1.png',
                    baseWidth: 260,
                    lgWidth: 650,
                },
            ],
        },
    },
    {
        contentsCd: '21',

        showTutorialVideo: false,
        connectorOverView: {
            connector_name: '気圧センサー',
            description: '周囲の気圧を測る',
            connector_detail_1: '気圧（地球上の空気の重さによる圧力）の強さを測ることができます',
            connector_detail_2: '上空に行くほど気圧が低くなることを利用して、高度を測ることもできます',
        },
        codeExample: {
            images: [
                {
                    title: '気圧を測定する',
                    codeBlockFileName: 'code-1-l1.png',
                    connectionFileName: 'code-1-r1.png',
                    baseWidth: 260,
                    lgWidth: 650,
                },
                {
                    title: '気圧が下がったら音を鳴らす',
                    codeBlockFileName: 'code-2-l1.png',
                    connectionFileName: 'code-2-r1.png',
                    baseWidth: 260,
                    lgWidth: 650,
                },
                {
                    title: '気圧が上がったら一定の間LEDを消す',
                    codeBlockFileName: 'code-3-l1.png',
                    connectionFileName: 'code-3-r1.png',
                    baseWidth: 260,
                    lgWidth: 650,
                },
            ],
        },
    },
    {
        contentsCd: '22',

        showTutorialVideo: false,
        connectorOverView: {
            connector_name: '水温センサー',
            description: '水の温度を測る',
            connector_detail_1: '温度で抵抗値が変化する素子により水の温度を測ることができます',
            connector_detail_2: '車のエンジン冷却水を監視して、オーバーヒートを防ぐときにも使われます',
        },
        codeExample: {
            images: [
                {
                    title: '水温を7SEGに表示し、さらにグラフモニターで表示する',
                    codeBlockFileName: 'code-1-l1.png',
                    connectionFileName: 'code-1-r1.png',
                    baseWidth: 260,
                    lgWidth: 650,
                },
                {
                    title: '水温が20℃以下になったら音を鳴らす',
                    codeBlockFileName: 'code-2-l1.png',
                    connectionFileName: 'code-2-r1.png',
                    baseWidth: 260,
                    lgWidth: 650,
                },
                {
                    title: '水温の違いによって、光り方を変える',
                    codeBlockFileName: 'code-3-l1.png',
                    connectionFileName: 'code-3-r1.png',
                    baseWidth: 260,
                    lgWidth: 650,
                },
            ],
        },
    },
    {
        contentsCd: '23',

        showTutorialVideo: false,
        connectorOverView: {
            connector_name: '人感センサー',
            description: '周りに人がいるかどうかを判断する',
            connector_detail_1: '人感センサーの周囲に動きがあるかどうかを測ることができます',
            connector_detail_2: '人感センサーの近くで人が動くと1、じっとしていると0を示します',
        },
        codeExample: {
            images: [
                {
                    title: '人が近くにいるかどうかをシリアルモニターで確認する',
                    codeBlockFileName: 'code-1-l1.png',
                    connectionFileName: 'code-1-r1.png',
                    baseWidth: 260,
                    lgWidth: 650,
                },
                {
                    title: '人がずっと近くにいると、フルカラーLEDの色が変わる',
                    codeBlockFileName: 'code-2-l1.png',
                    connectionFileName: 'code-2-r1.png',
                    baseWidth: 260,
                    lgWidth: 650,
                },
            ],
        },
    },
    {
        contentsCd: '24',

        showTutorialVideo: false,
        connectorOverView: {
            connector_name: '圧力センサー',
            description: 'センサーにかかる圧力を測る',
            connector_detail_1: 'センサーの表面に対して面積あたりに垂直に働く力の大きさを測ることができます',
            connector_detail_2: '力が同じでも、面積が小さければ圧力は大きく、面積が大きければ圧力は小さくなります',
        },
        codeExample: {
            images: [
                {
                    title: '圧力を測定する',
                    codeBlockFileName: 'code-1-l1.png',
                    connectionFileName: 'code-1-r1.png',
                    baseWidth: 260,
                    lgWidth: 650,
                },
                {
                    title: '圧力を加えたらLEDが光る',
                    codeBlockFileName: 'code-2-l1.png',
                    connectionFileName: 'code-2-r1.png',
                    baseWidth: 260,
                    lgWidth: 650,
                },
                {
                    title: '圧力の値によって音を変える',
                    codeBlockFileName: 'code-3-l1.png',
                    connectionFileName: 'code-3-r1.png',
                    baseWidth: 260,
                    lgWidth: 650,
                },
            ],
        },
    },
    {
        contentsCd: '25',

        showTutorialVideo: false,
        connectorOverView: {
            connector_name: 'アルコールセンサー',
            description: '呼気中や血中のアルコール濃度を測る',
            connector_detail_1: 'アルコールセンサーでアルコールの呼気中濃度、血中濃度を測ることができます',
            connector_detail_2: '息に含まれるアルコールの濃度によって音が変わるセンサーをつくることができます',
        },
        codeExample: {
            images: [
                {
                    title: '息にアルコールが含まれているかを測定する',
                    codeBlockFileName: 'code-1-l1.png',
                    connectionFileName: 'code-1-r1.png',
                    baseWidth: 260,
                    lgWidth: 650,
                },
                {
                    title: '呼気中アルコール濃度によってなる音の高さを変える',
                    codeBlockFileName: 'code-2-l1.png',
                    connectionFileName: 'code-2-r1.png',
                    baseWidth: 260,
                    lgWidth: 650,
                },
            ],
        },
    },
    {
        contentsCd: '26',

        showTutorialVideo: false,
        connectorOverView: {
            connector_name: '曲げセンサー',
            description: 'センサーの曲がり具合を測る',
            connector_detail_1: '曲げの角度や度合いに応じて変化する電気抵抗を検知して曲がり具合を測ることができます',
            connector_detail_2: '大きく曲がれば曲がるほど導電素子の密度が高まり、数値が大きくなります',
        },
        codeExample: {
            images: [
                {
                    title: 'どれだけ曲げたかを数値で表示する',
                    codeBlockFileName: 'code-1-l1.png',
                    connectionFileName: 'code-1-r1.png',
                    baseWidth: 260,
                    lgWidth: 650,
                },
                {
                    title: '曲げすぎたときに音を鳴らす',
                    codeBlockFileName: 'code-2-l1.png',
                    connectionFileName: 'code-2-r1.png',
                    baseWidth: 260,
                    lgWidth: 650,
                },
                {
                    title: '曲げ具合によって、鳴る音の高さを変える',
                    codeBlockFileName: 'code-3-l1.png',
                    connectionFileName: 'code-3-r1.png',
                    baseWidth: 260,
                    lgWidth: 650,
                },
            ],
        },
    },
    {
        contentsCd: '27',

        showTutorialVideo: false,
        connectorOverView: {
            connector_name: 'GPSセンサー',
            description: 'GPSセンサーのある位置の緯度と経度を測る',
            connector_detail_1: '全地球測位システムにより緯度、経度、高度を測ることができます',
            connector_detail_2: '複数のGPS衛星信号を受信することで高い精度で位置を調べることができます',
        },
        codeExample: {
            images: [
                {
                    title: '緯度と経度をシリアルモニターに表示する',
                    codeBlockFileName: 'code-1-l1.png',
                    connectionFileName: 'code-1-r1.png',
                    baseWidth: 260,
                    lgWidth: 650,
                },
                {
                    title: 'スイッチを押したときの緯度と経度を表示する',
                    codeBlockFileName: 'code-2-l1.png',
                    connectionFileName: 'code-2-r1.png',
                    baseWidth: 260,
                    lgWidth: 650,
                },
            ],
        },
    },
    {
        contentsCd: '28',

        showTutorialVideo: false,
        connectorOverView: {
            connector_name: '水流センサー',
            description: 'センサー内部の羽根車の回転数から水流の変化を計測できる',
            connector_detail_1: '流れた水の量を羽根車の回転数として計測できます',
            connector_detail_2: '羽根車の秒間回転数から水流の速さを数値として計測できます',
        },
        codeExample: {
            images: [
                {
                    title: '水が流れているかどうかを確認する',
                    codeBlockFileName: 'code-1-l1.png',
                    connectionFileName: 'code-1-r1.png',
                    baseWidth: 260,
                    lgWidth: 650,
                },
                {
                    title: '流れる水の勢いが指定した羽根車の秒間回転数を超えた時にLEDを光らせる',
                    codeBlockFileName: 'code-2-l1.png',
                    connectionFileName: 'code-2-r1.png',
                    baseWidth: 260,
                    lgWidth: 650,
                },
            ],
        },
    },
    {
        contentsCd: '29',

        showTutorialVideo: false,
        connectorOverView: {
            connector_name: 'アンモニアセンサー',
            description: '空気中のアンモニア濃度を測る',
            connector_detail_1: 'アンモニアセンサーで空気中のアンモニア濃度を測ることができます',
            connector_detail_2: '空気中のアンモニア濃度によって色が変わるセンサーをつくることができます',
        },
        codeExample: {
            images: [
                {
                    title: '空気中のアンモニア濃度（PPM）を測定する',
                    codeBlockFileName: 'code-1-l1.png',
                    connectionFileName: 'code-1-r1.png',
                    baseWidth: 260,
                    lgWidth: 650,
                },
                {
                    title: '空気中のアンモニア濃度（PPM）が5を超えたらフルカラーLEDを緑から黄色に変化させ、10を超えたら赤に変化させる',
                    codeBlockFileName: 'code-2-l1.png',
                    connectionFileName: 'code-2-r1.png',
                    baseWidth: 260,
                    lgWidth: 650,
                },
            ],
        },
    },
    {
        contentsCd: '30',

        showTutorialVideo: false,
        connectorOverView: {
            connector_name: 'トグルスイッチ',
            description: 'トグルスイッチの「ON」「ON2」「OFF」の状態を判断する',
            connector_detail_1: 'トグルスイッチで「ON」と「OFF」を測ることができます',
            connector_detail_2: '「ON」と「ON2」を切り替えることができます',
        },
        codeExample: {
            images: [
                {
                    title: '「ON」にすると青く光り、「ON2」にすると緑に光る',
                    codeBlockFileName: 'code-1-l1.png',
                    connectionFileName: 'code-1-r1.png',
                    baseWidth: 260,
                    lgWidth: 650,
                },
                {
                    title: 'トグルスイッチを「ON」にしてスイッチを押すと7SEGの数値が1増えて、「ON2」にしてスイッチを押すと10増えるようにする',
                    codeBlockFileName: 'code-2-l1.png',
                    connectionFileName: 'code-2-r1.png',
                    baseWidth: 260,
                    lgWidth: 650,
                },
            ],
        },
    },
    {
        contentsCd: '31',

        showTutorialVideo: false,
        connectorOverView: {
            connector_name: 'スライドスイッチ',
            description: 'スライドスイッチの「ON」「OFF」の状態を判断する',
            connector_detail_1: 'つまみを動かして「ON」と「OFF」を切り替えることができます',
            connector_detail_2: 'つまみを動かすことで内部の接点を物理的に移動させて回路を開閉します',
        },
        codeExample: {
            images: [
                {
                    title: 'スライドスイッチが「ON」の時だけLEDが光り、「OFF」になると消える',
                    codeBlockFileName: 'code-1-l1.png',
                    connectionFileName: 'code-1-r1.png',
                    baseWidth: 260,
                    lgWidth: 650,
                },
                {
                    title: 'スライドスイッチが「ON」になっている時間の合計を7SEGに表示する',
                    codeBlockFileName: 'code-2-l1.png',
                    connectionFileName: 'code-2-r1.png',
                    baseWidth: 260,
                    lgWidth: 650,
                },
            ],
        },
    },
    {
        contentsCd: '32',

        showTutorialVideo: false,
        connectorOverView: {
            connector_name: 'フェーダー',
            description: 'フェーダーの位置によって数字を出力する',
            connector_detail_1:
                '最小値と最大値を指定することで、フェーダーの位置に応じてその範囲の値を指定することができます',
            connector_detail_2: 'つまみが下の方だと最小値、上の方だと最大値を出力できます',
        },
        codeExample: {
            images: [
                {
                    title: 'シリアルモニターに数値を出力する',
                    codeBlockFileName: 'code-1-l1.png',
                    connectionFileName: 'code-1-r1.png',
                    baseWidth: 260,
                    lgWidth: 650,
                },
                {
                    title: 'フェーダーを動かして、フルカラーLEDの色を変化させる',
                    codeBlockFileName: 'code-2-l1.png',
                    connectionFileName: 'code-2-r1.png',
                    baseWidth: 260,
                    lgWidth: 650,
                },
                {
                    title: 'フェーダーを動かして、音を変化させる',
                    codeBlockFileName: 'code-3-l1.png',
                    connectionFileName: 'code-3-r1.png',
                    baseWidth: 260,
                    lgWidth: 650,
                },
            ],
        },
    },
    {
        contentsCd: '33',

        showTutorialVideo: false,
        connectorOverView: {
            connector_name: '振動モーター',
            description: '振動モーターを振動させる',
            connector_detail_1: '振動モーターをずっと震わせることができます',
            connector_detail_2: '交互に震えさせてリズムをとることができます',
        },
        codeExample: {
            images: [
                {
                    title: '振動モーターを動かす',
                    codeBlockFileName: 'code-1-l1.png',
                    connectionFileName: 'code-1-r1.png',
                    baseWidth: 260,
                    lgWidth: 650,
                },
                {
                    title: 'タッチセンサーで振動モーターをふるわせたり、止めたりを切り替える',
                    codeBlockFileName: 'code-2-l1.png',
                    connectionFileName: 'code-2-r1.png',
                    baseWidth: 260,
                    lgWidth: 650,
                },
                {
                    title: '明るさによって振動するリズムを変える',
                    codeBlockFileName: 'code-3-l1.png',
                    connectionFileName: 'code-3-r1.png',
                    baseWidth: 260,
                    lgWidth: 650,
                },
            ],
        },
    },
    {
        contentsCd: '34',

        showTutorialVideo: false,
        connectorOverView: {
            connector_name: 'プロペラモーター',
            description: 'プロペラを回したり止めたりする',
            connector_detail_1: 'プロペラをずっと回し続けることができます',
            connector_detail_2: '「止まる」ことでプロペラを止めることができます',
        },
        codeExample: {
            images: [
                {
                    title: 'プロペラを回してみる',
                    codeBlockFileName: 'code-1-l1.png',
                    connectionFileName: 'code-1-r1.png',
                    baseWidth: 260,
                    lgWidth: 650,
                },
                {
                    title: '暑い時にプロペラが回るようにする',
                    codeBlockFileName: 'code-2-l1.png',
                    connectionFileName: 'code-2-r1.png',
                    baseWidth: 260,
                    lgWidth: 650,
                },
            ],
        },
    },
    {
        contentsCd: '35',

        showTutorialVideo: false,
        connectorOverView: {
            connector_name: 'OLED',
            description: 'OLEDに数値や文字を表示する',
            connector_detail_1: 'OLEDにセンサーの値を表示することができます',
            connector_detail_2: 'アルファベットも表示することができます',
        },
        codeExample: {
            images: [
                {
                    title: 'OLEDで部屋の明るさを表示する',
                    codeBlockFileName: 'code-1-l1.png',
                    connectionFileName: 'code-1-r1.png',
                    baseWidth: 260,
                    lgWidth: 650,
                },
                {
                    title: '部屋が明るかったら「bright」、暗かったら「dark」と表示する',
                    codeBlockFileName: 'code-2-l1.png',
                    connectionFileName: 'code-2-r1.png',
                    baseWidth: 260,
                    lgWidth: 650,
                },
            ],
        },
    },
    {
        contentsCd: '36',

        showTutorialVideo: false,
        connectorOverView: {
            connector_name: '180°回転サーボモーター',
            description: '180°サーボモーターの角度を制御する',
            connector_detail_1: 'サーボモーターの角度を0°~180°の間で決めて動かすことができます',
            connector_detail_2: 'ある位置で角度を決めて回転サーボモーターを止めることができます',
        },
        codeExample: {
            images: [
                {
                    title: 'サーボモーターの角度を90°にする',
                    codeBlockFileName: 'code-1-l1.png',
                    connectionFileName: 'code-1-r1.png',
                    baseWidth: 260,
                    lgWidth: 650,
                },
                {
                    title: '振り子の動きを作る',
                    codeBlockFileName: 'code-2-l1.png',
                    connectionFileName: 'code-2-r1.png',
                    baseWidth: 260,
                    lgWidth: 650,
                },
                {
                    title: '温度が上がったら知らせる装置を作る',
                    codeBlockFileName: 'code-3-l1.png',
                    connectionFileName: 'code-3-r1.png',
                    baseWidth: 260,
                    lgWidth: 650,
                },
            ],
        },
    },
    {
        contentsCd: '37',

        showTutorialVideo: false,
        connectorOverView: {
            connector_name: 'ポンプ',
            description: 'ポンプを動かして水を出したり止めたりする',
            connector_detail_1: 'ポンプをずっと動かして水を出し続けることができます',
            connector_detail_2: 'センサーを使って水を供給できます',
        },
        codeExample: {
            images: [
                {
                    title: 'タッチセンサーでポンプから出る水をコントロールする',
                    codeBlockFileName: 'code-1-l1.png',
                    connectionFileName: 'code-1-r1.png',
                    baseWidth: 260,
                    lgWidth: 650,
                },
                {
                    title: '土の水分量が減ったら水を追加する',
                    codeBlockFileName: 'code-2-l1.png',
                    connectionFileName: 'code-2-r1.png',
                    baseWidth: 260,
                    lgWidth: 650,
                },
            ],
        },
    },
    {
        contentsCd: '38',

        showTutorialVideo: false,
        connectorOverView: {
            connector_name: 'LEDバー',
            description: '10メモリあるLEDバーの光らせる範囲を指定する',
            connector_detail_1: '最小値と最大値を指定することでその範囲のLEDを光らせることができます',
            connector_detail_2: '全て光らないを選択することで範囲に関係なく全て消すことができます',
        },
        codeExample: {
            images: [
                {
                    title: '1番目から10番目のLEDを光らせる',
                    codeBlockFileName: 'code-1-l1.png',
                    connectionFileName: 'code-1-r1.png',
                    baseWidth: 260,
                    lgWidth: 650,
                },
                {
                    title: 'だんだんLEDの光る範囲を変更する',
                    codeBlockFileName: 'code-2-l1.png',
                    connectionFileName: 'code-2-r1.png',
                    baseWidth: 260,
                    lgWidth: 650,
                },
                {
                    title: 'タッチセンサーをタッチすると点灯範囲が増えていくようにする',
                    codeBlockFileName: 'code-3-l1.png',
                    connectionFileName: 'code-3-r1.png',
                    baseWidth: 260,
                    lgWidth: 650,
                },
            ],
        },
    },
    {
        contentsCd: '39',

        showTutorialVideo: false,
        connectorOverView: {
            connector_name: 'TTモーター',
            description: 'TTモーターを回したり止めたりする',
            connector_detail_1: 'TTモーターを回したり止めたりできます',
            connector_detail_2: '回る方向を決めることができます',
        },
        codeExample: {
            images: [
                {
                    title: 'モーターを回す',
                    codeBlockFileName: 'code-1-l1.png',
                    connectionFileName: 'code-1-r1.png',
                    baseWidth: 260,
                    lgWidth: 650,
                },
                {
                    title: '明るいところと暗いところを往復する',
                    codeBlockFileName: 'code-2-l1.png',
                    connectionFileName: 'code-2-r1.png',
                    baseWidth: 260,
                    lgWidth: 650,
                },
            ],
        },
    },
    {
        contentsCd: '40',

        showTutorialVideo: false,
        connectorOverView: {
            connector_name: 'SDカード',
            description: 'SDカードにセンサーの値などを書き込む',
            connector_detail_1: 'センサーから受け取った値をファイルに書き込むことができます',
            connector_detail_2: '書き込んだファイルを消去できます',
        },
        codeExample: {
            images: [
                {
                    title: 'SDカードに明るさセンサーの値を書き込む',
                    codeBlockFileName: 'code-1-l1.png',
                    connectionFileName: 'code-1-r1.png',
                    baseWidth: 260,
                    lgWidth: 650,
                },
                {
                    title: '書き込んだファイルを消去する',
                    codeBlockFileName: 'code-2-l1.png',
                    connectionFileName: 'code-2-r1.png',
                    baseWidth: 260,
                    lgWidth: 650,
                },
                {
                    title: 'SDカードに回数のデータを記録する',
                    codeBlockFileName: 'code-3-l1.png',
                    connectionFileName: 'code-3-r1.png',
                    baseWidth: 260,
                    lgWidth: 650,
                },
            ],
        },
    },
];

const contentCdToConnectorType: Record<string, ConnectorType> = Object.fromEntries(
    Object.entries(contentCdMap).map(([type, cd]) => [cd, type]),
) as Record<string, ConnectorType>;

export const BREADCRUMB_CONNECTOR_GUIDE_ITEMS: Record<string, BreadcrumbItem[]> = {
    [BASE_URL]: [{ label: `${CONTENTSET_TITLE} 目次` }],
    ...Object.fromEntries(
        CONNECTOR_GUIDE_DATA.map((item) => [
            `${BASE_URL}/${contentCdToConnectorType[item.contentsCd]}`,
            [{ label: CONTENTSET_TITLE, href: BASE_URL }, { label: item.connectorOverView.connector_name }],
        ]),
    ),
};
